import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Popover } from 'antd';
import { Callout, Chip, type ColumnConfig, type DropdownMenuItem } from '@/components/ui';
import { AccountsTable as SharedAccountsTable } from '@/pages/Console/Accounts/Shared/AccountsTable';
import { IdentityCell, StatusChip, type CellTone } from '@/pages/Console/Accounts/Shared/cells';
import type { ScopedAccountRow } from '@/api/orgAdmin';
import { InlineLevelPicker } from './InlineLevelPicker';
import type { InlineLevelPickerVM } from './useInlineLevelPicker';
import { isUnassignedRoleKey } from '@/pages/Console/Accounts/Shared/roleKeyDisplay';
import type { ScopedAccountGroupedRow, ScopedAccountStatus } from './types';
import type { ScopedAccountsVM } from './useScopedAccounts';
import type { ScopedAccountViewsVM } from './useScopedAccountViews';
import type { ScopedAccountWritesVM } from './useScopedAccountWrites';
import { useScopedAccountUsernames } from './useScopedAccountUsernames';
import shared from './ScopedAccounts.module.css';
import styles from './ScopedAccountsTable.module.css';

/** Priority order matters: a revoked grant is "Đã ngưng" whatever its sync state says. */
function statusOf(row: ScopedAccountRow): ScopedAccountStatus {
    if (!row.is_active) return 'revoked';
    if (row.sync_status === 'error') return 'sync-error';
    if (row.sync_status === 'pending') return 'pending';
    return 'ok';
}

function statusToneOf(status: ScopedAccountStatus): CellTone {
    return status === 'ok' ? 'ok' : status === 'pending' ? 'warn' : 'off';
}

/**
 * EAA-1839 (Level column rework): what the chip itself shows. `role_key` (e.g.
 * `unassigned-ach`) is the field that actually DIFFERS between rows in this
 * tenant's real data — every `level_name` in an org reads close to the same
 * long auto-generated string, "Unassigned (ach, auto-synced pending role
 * mapping)"/"Unassigned (lms, auto-synced pending role mapping)", which at a
 * 140px column clamps to an identical "Unassigned (ac" on every row and
 * distinguishes nothing. `role_key` is short enough to render in full at that
 * width and is the one thing worth scanning down the column for, so it
 * replaces `level_name` as the chip's text; `level_name` moves to the
 * `Popover` below rather than being dropped. Falls back to `level_name` only
 * when `role_key` is null/blank (the type is `string | null` on
 * `OrgAssignment`, `api/orgAdmin.ts`) so the chip is never empty.
 */
function levelChipTextOf(row: ScopedAccountRow): string {
    const roleKey = row.role_key?.trim();
    return roleKey ? roleKey : (row.level_name as string);
}

/**
 * EAA-1839 (Level column rework): the full detail the clamped chip hides —
 * always both `level_name` and `role_key` (superset of whichever one the chip
 * is not currently showing), so nothing the chip omits is lost.
 */
function levelPopoverContent(row: ScopedAccountRow, t: TFunction): JSX.Element {
    return (
        <span className={styles.popoverContent}>
            <span className={styles.popoverContentName}>{row.level_name}</span>
            {row.role_key && (
                <span className={styles.popoverContentRole}>
                    {t('console.hierarchy.scopedAccounts.table.levelPopoverRoleKey')}
                    {': '}
                    <code className={styles.popoverContentRoleCode}>{row.role_key}</code>
                </span>
            )}
        </span>
    );
    // EAA-1839: deliberately NOT including `sync_status` here — the grant's
    // provisioning state already has its own dedicated "Tình trạng" column
    // (`sync_status`) with its own status chip a few cells over. Repeating it
    // here would duplicate, not add, information; this popover's job is to
    // surface what the clamped chip hides (the full name, and the role key
    // when the chip isn't already showing it), not to become a second status
    // readout.
}

/**
 * EAA-1839: `OrgAssignment` (see `api/orgAdmin.ts`'s contract note above
 * `ScopedAccountRow`) carries `user_sub` + `user_email` and NOTHING else — no
 * display name, no username, because this endpoint has no Keycloak join. A row
 * with an email is identified by that email alone. A row with NO email would
 * otherwise show only the "(chưa có email)" placeholder, which identifies
 * nobody — the sub is then the only value left that distinguishes the row, so
 * it is shown here (truncated, matching the org-id fallback elsewhere in this
 * file) rather than dropped. Never derive a username from the email's local
 * part: that would fabricate identity data on a security screen.
 *
 * EAA-1839 (identity-column restructure): this feeds the identity cell's
 * FALLBACK, not its primary line. `identityPrimaryOf` prefers the username the
 * capped realm join supplies and only falls back to this email — see that
 * function. An earlier revision of this comment claimed the email WAS the
 * primary line, which was true for exactly one pass, before the user asked for
 * "username first, email below"; it is kept corrected rather than deleted
 * because the email-primary shape is easy to reintroduce.
 *
 * REVERSED 2026-08-24 (user request: "show first name last name on top,
 * username below — if not have, only username"). This block used to end
 * "there is no display name on this endpoint to put above either" and "Keycloak
 * simply has no names set on these accounts in this realm", concluding from
 * ONE account's profile modal (`gvanhdo`, which genuinely has neither) that NO
 * account in the realm has one. That was an overgeneralization from a single
 * example: the SAME realm walk `usernameBySub` already performs returns
 * `first_name`/`last_name` populated on many other rows (verified live in the
 * network panel — e.g. "Thinh Nguyen", "Bạch Vũ") — the true constraint was
 * only that a per-row `GET /accounts/{sub}` to fetch them one at a time (20
 * requests per page render) was too expensive, never that the field is always
 * empty. `useScopedAccountUsernames.displayNameBySub` reads those two fields
 * off rows already in hand, at no extra request, and the identity cell now
 * prefers a real name over the username exactly the way the realm board's own
 * `accountDisplayName()` chain does — see `identityPrimaryOf`/
 * `identitySecondaryOf` below.
 *
 * PRIORITY, current: name (first + last) → username → email (row's own, then
 * the realm join's) → a truncated sub. The dedicated `email` column still
 * exists for scanning, sorting and copying regardless of what the identity
 * cell's own lines show.
 */
function identityPrimaryOf(
    row: ScopedAccountRow,
    displayNameBySub: Map<string, string>,
    emailBySub: Map<string, string>,
    t: TFunction
): string {
    // EAA-1839 (2026-08-24, user request): `displayNameBySub` already resolves
    // to a real name when Keycloak has one and to the bare username otherwise
    // (`accountDisplayName`'s own fallback) — so this one lookup covers both
    // "show the name" and "if not have first/last name, only username". When
    // the join is unavailable (no `console.accounts.view`, or the sub is not on
    // the realm page reached) there is no entry at all, so the email takes the
    // primary line rather than leaving it blank; a truncated sub is the last
    // resort, because a row identifying nobody is worse than an ugly one.
    const displayName = displayNameBySub.get(row.user_sub);
    if (displayName) return displayName;
    // EAA-1839: `row.user_email` first — it is the assignment's OWN denormalized
    // value, and preferring it over the realm join keeps this cell agreeing with
    // the `email` column, which has no join to fall back on. `emailBySub` (the
    // same realm walk `usernameBySub` comes from) only fires when that snapshot
    // is blank — see `emailBySub`'s own docstring for why it can be.
    if (row.user_email) return row.user_email;
    const joinedEmail = emailBySub.get(row.user_sub);
    if (joinedEmail) return joinedEmail;
    return row.user_sub
        ? `${row.user_sub.slice(0, 8)}…`
        : t('console.hierarchy.scopedAccounts.table.noEmail');
}

/**
 * EAA-1839 (frame 34, "Tài khoản" column, second line): the joined
 * `username`, from `useScopedAccountUsernames` — a single cached realm-list
 * request, NOT a per-row fetch (see that hook's docstring for the full
 * contract). Renders NOTHING (not a placeholder) when the caller lacks
 * `console.accounts.view` or the sub is absent from the join — either case is
 * a real, honest gap, not a value to fake.
 */
function identitySecondaryOf(
    row: ScopedAccountRow,
    usernameBySub: Map<string, string>,
    displayNameBySub: Map<string, string>,
    emailBySub: Map<string, string>
): string | undefined {
    const displayName = displayNameBySub.get(row.user_sub);
    const username = usernameBySub.get(row.user_sub);
    // EAA-1839 (2026-08-24, user request): username is the SECOND line — but
    // only when the primary line is an actual NAME. `accountDisplayName`
    // resolves to the username itself when Keycloak has no name, and printing
    // that same string again below would be the exact duplicate this function
    // already existed to prevent — matching the realm board's own
    // `AccountsListTable`: `secondary={primary === row.username ? undefined :
    // row.username}`.
    if (displayName && username && displayName !== username) {
        return username;
    }
    // No real name: fall through to the email, exactly as before this pass —
    // the assignment's own snapshot first, the realm join only when that
    // snapshot is blank.
    if (!username) return undefined;
    const email = row.user_email || emailBySub.get(row.user_sub);
    if (!email) return undefined;
    // EAA-1839: a Keycloak username can BE the account's email address (e.g.
    // "admin@admin.com" as both). Without this check the cell printed that
    // same string twice, once as the primary line and once as the second —
    // seen in a real screenshot. Compared case-insensitively and trimmed
    // because Keycloak usernames are case-insensitive for login purposes.
    if (username.trim().toLowerCase() === email.trim().toLowerCase()) {
        return undefined;
    }
    return email;
}

/**
 * EAA-1839 (2026-08-24, user request): the Đơn vị cell for a grouped row shows
 * the FULL "org1, org2, org3" list whenever the column is wide enough to hold
 * it — including after a manual resize — and only collapses to a plain
 * "orgName +N" summary when the full text genuinely would not fit. This is a
 * measurement, not a guess: `table-layout: fixed` gives the cell a definite
 * width regardless of content, so whether the full list fits is a fact about
 * the rendered DOM, not something CSS `text-overflow: ellipsis` alone can
 * answer without also deciding WHAT to truncate to. A `ResizeObserver` on the
 * cell re-checks on every resize (manual drag included), so widening the
 * column live flips a truncated row back to its full list without a reload.
 *
 * The measurement always compares the FULL text's rendered width against the
 * cell's current width — never the currently-displayed (possibly already
 * summarized) string — because column width does not change with content
 * under `table-layout: fixed`, so the full text's fit is stable to check
 * regardless of what is currently on screen.
 */
function OrgListCell({ orgNames, title }: { orgNames: string[]; title: string }): JSX.Element {
    const containerRef = useRef<HTMLSpanElement>(null);
    // EAA-1839: the measurer always holds the FULL text — never whatever is
    // currently displayed. Measuring the currently-rendered string would be
    // self-defeating the moment it is already the short "+N" summary: a
    // `ResizeObserver` firing on a still-narrow column would find the SHORT
    // summary fits (it always does) and flip back to the full list it had
    // already determined does not fit, oscillating on every resize tick.
    const measureRef = useRef<HTMLSpanElement>(null);
    const [fits, setFits] = useState(true);
    const fullText = orgNames.join(', ');

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;
        const check = () => setFits(measure.scrollWidth <= container.clientWidth + 1);
        check();
        // Observes the CONTAINER, not the measurer — the measurer's own size never
        // changes (it always holds the same full text), only the container's width
        // does, on a manual column resize.
        const observer = new ResizeObserver(check);
        observer.observe(container);
        return () => observer.disconnect();
    }, [fullText]);

    return (
        <span ref={containerRef} className={styles.orgCell} title={title}>
            <span ref={measureRef} className={styles.orgCellMeasure} aria-hidden="true">
                {fullText}
            </span>
            {fits ? (
                fullText
            ) : (
                <>
                    {orgNames[0]}
                    <span className={styles.orgCellMore}> +{orgNames.length - 1}</span>
                </>
            )}
        </span>
    );
}

function statusTextOf(t: TFunction, status: ScopedAccountStatus): string {
    switch (status) {
        case 'revoked':
            return t('console.hierarchy.scopedAccounts.table.statusRevoked');
        case 'sync-error':
            return t('console.hierarchy.scopedAccounts.table.statusSyncError');
        case 'pending':
            return t('console.hierarchy.scopedAccounts.table.statusPending');
        default:
            return t('console.hierarchy.scopedAccounts.table.statusOk');
    }
}

function rowActionItems(
    row: ScopedAccountGroupedRow,
    vm: ScopedAccountsVM,
    writes: ScopedAccountWritesVM,
    onTransfer: (row: ScopedAccountRow) => void,
    onAssignLevel: (row: ScopedAccountRow) => void,
    t: TFunction
): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [];
    // EAA-1839 (2026-08-24, "can make it is one rows"): every action below acts
    // on THIS row's OWN assignment — the most recently granted one — never on
    // the account as a whole, because `Assignment` is per (user, org) and
    // "revoke this account" is not an operation the backend has. On a grouped
    // row that is ambiguous from the label alone ("Thu hồi" could read as
    // "remove this person entirely"), so the label is qualified with the org
    // name whenever `row.group` says there is more than one to confuse it with.
    const orgName = vm.orgNameById.get(row.organization_id) ?? row.organization_id;
    const qualify = (label: string): string =>
        row.group ? t('console.hierarchy.scopedAccounts.table.actionAtOrg', { action: label, org: orgName }) : label;
    // EAA-1839 (frame 35a): the same inline Level assign the amber chip opens, also
    // reachable from the kebab — a row that already HAS a Level shows no chip, and
    // changing one is the commoner correction of the two.
    if (vm.canManage && row.is_active) {
        items.push({
            key: 'assign-level',
            label: qualify(
                row.level_name
                    ? t('console.hierarchy.scopedAccounts.table.actionChangeLevel')
                    : t('console.hierarchy.scopedAccounts.table.actionAssignLevel')
            ),
            onSelect: () => onAssignLevel(row),
        });
    }
    if (vm.canReset) {
        items.push({
            key: 'reset',
            label: t('console.hierarchy.scopedAccounts.table.actionResetPassword'),
            onSelect: () => writes.setResetTarget(row),
        });
    }
    // EAA-1820 (frame 85): "Chuyển đơn vị" moves the row's Assignment to another
    // đơn vị in place — same `canManage` gate as the other privileged row
    // actions on this table (`useScopedAccounts.canManage`), and only offered on
    // an active row for the same reason "Thu hồi" is: a revoked row has nothing
    // left to move.
    if (vm.canManage && row.is_active) {
        items.push({
            key: 'transfer',
            label: qualify(t('console.hierarchy.scopedAccounts.table.actionTransfer')),
            onSelect: () => onTransfer(row),
        });
    }
    if (vm.canManage && row.is_active) {
        items.push({
            key: 'revoke',
            label: qualify(t('console.hierarchy.scopedAccounts.table.actionRevoke')),
            danger: true,
            onSelect: () => writes.setRevokeTarget(row),
        });
    }
    return items;
}

/**
 * EAA-1820 (screen unification pass): the org accounts columns, now rendered
 * through the SAME `shared/AccountsTable` the realm screen uses (was a
 * hand-rolled `<table>` with its own checkbox/sort-header/status-dot markup —
 * see this file's git history / EAA-1839 for that version). Column CONTENT
 * stays org-specific:
 *
 *  - TÀI KHOẢN's primary line is email (or a truncated `user_sub` fallback),
 *    NOT name + username + email — `AssignmentSerializer` exposes
 *    `user_sub`/`user_email` only, no Keycloak join on this endpoint, so a
 *    display name (last name + first name) would need a per-row
 *    `GET /accounts/{sub}` (20 requests per page render) and is deliberately
 *    NOT built here; inventing one from the email/username would be a
 *    fabrication this ticket forbids. Its second line is `username`, joined
 *    client-side from ONE cached realm-list request
 *    (`useScopedAccountUsernames`) — never a per-row fetch, and rendered as
 *    nothing (not a placeholder) when the caller lacks
 *    `console.accounts.view` or the sub is not on that realm page. EMAIL now
 *    has its OWN column too (`email`), fixed-width and truncating with a
 *    native `title` — see `identityPrimaryOf`'s comment for why it still also
 *    anchors the identity cell.
 *  - LEVEL & VAI TRÒ is the org-only column (vs. the realm's "Vai trò ứng dụng"):
 *    `level_name` plus the org's own resolved app-role key. An EMPTY one renders
 *    frame 35's amber "Chọn Level" chip, which opens frame 35a's popover in place
 *    (`InlineLevelPicker`) rather than only naming the gap.
 *  - ĐĂNG NHẬP and NGUỒN both render "Không có dữ liệu": Keycloak's user list API
 *    exposes no last-login instant and `AssignmentSerializer` carries no
 *    provenance field, and this endpoint has no Keycloak join at all to ask. Both
 *    columns are KEPT (and hideable from frame 35's "Cột" menu) rather than
 *    dropped, because frame 35 draws them and a later backend fills them here.
 *
 * Only `user_sub` and `sync_status` are sortable — `ALLOWED_ORDER_FIELDS`.
 *
 * EAA-1839 (frame 35 completion pass): column visibility and row density come from
 * `useScopedAccountViews`, and the two row tints frame 35 draws (a paused/withdrawn
 * Level, a revoked grant) are applied through `rowClassName`.
 */
export function ScopedAccountsTable({
    vm,
    views,
    writes,
    picker,
    onTransfer,
    onViewDetail,
}: {
    vm: ScopedAccountsVM;
    views: ScopedAccountViewsVM;
    writes: ScopedAccountWritesVM;
    /** Frame 35a — also the target of the kebab's "Gán Level". */
    picker: InlineLevelPickerVM;
    /** EAA-1820 (frame 85): opens `RowTransferDialog` for this row. */
    onTransfer: (row: ScopedAccountRow) => void;
    /** Frame 35b: opens the account profile drawer. */
    onViewDetail: (row: ScopedAccountRow) => void;
}) {
    const { t } = useTranslation();
    const { usernameBySub, displayNameBySub, emailBySub, capped: usernameJoinCapped } =
        useScopedAccountUsernames();

    const allColumns: (ColumnConfig<ScopedAccountRow> & { hideKey?: string })[] = [
        {
            key: 'user_sub',
            title: t('console.hierarchy.scopedAccounts.table.columnAccount'),
            sorter: true,
            /*
             * EAA-1839: this column is the row's identity and must never be the one
             * that collapses — and it DID collapse, to exactly 0px, until this width
             * existed. Measured in the running app at 1366x768: the declared widths
             * of the other columns summed to 986px against a 978px table, so under
             * antd's `table-layout: fixed` (which `DataTable` turns on via its
             * default `scroll.x`) there was no leftover for a width-less column. The
             * cell's avatar then overlapped its own text and the header's "Tài khoản"
             * wrapped one letter per line, making the header row 199px tall.
             *
             * The earlier comment here claimed the opposite — that omitting `width`
             * let this column flex and absorb slack, so it could not be squeezed.
             * That is true under `table-layout: auto`. Under `fixed` a width-less
             * column is the FIRST thing to reach zero. Keep a number here, and keep
             * the whole set summing to less than the available width (see the note
             * on `email` below).
             *
             * EAA-1839 (compact selection column): 234, not 230 — `AccountsTable`
             * now passes `compactSelectionColumn` to `DataTable`, shrinking the
             * checkbox column from ~36px to 32px (see `DataTable.types.ts`). The
             * freed 4px go here rather than sitting unallocated, per the same
             * "the identity column gets whatever is spare" rule as `email` below.
             */
            width: 234,
            render: (_value, row) => (
                <IdentityCell
                    primary={identityPrimaryOf(row, displayNameBySub, emailBySub, t)}
                    secondary={identitySecondaryOf(row, usernameBySub, displayNameBySub, emailBySub)}
                />
            ),
        },
        {
            // EAA-1839 (identity-column restructure): `user_email` split out into
            // its own column so it can be scanned/sorted/copied on its own — see
            // `identityPrimaryOf`'s comment for why the identity cell ALSO keeps
            // showing it as its primary line rather than this column replacing
            // that use. Fully available today: `AssignmentSerializer` carries
            // `user_email` directly, no join needed.
            key: 'email',
            /* EAA-1839: the widths below are a SET, not independent numbers — they
             * have to sum (plus the 32px compact selection column and the actions
             * column) to
             * less than the table's available width, or `table-layout: fixed` starves
             * whichever column has none. Budget at 1366x768 with the 236px rail
             * showing: ~978px. */
            width: 160,
            title: t('console.hierarchy.scopedAccounts.table.columnEmail'),
            render: (_value, row) => {
                if (row.user_email) {
                    return (
                        <span className={styles.emailCell} title={row.user_email}>
                            {row.user_email}
                        </span>
                    );
                }
                // EAA-1839: `Assignment.user_email` is a write-time snapshot the
                // backend can leave blank (`bulk_apply` creates rows without one —
                // see `emailBySub`'s docstring); the realm join supplies the real
                // address when it has one, marked with the module's `.emailCellJoined` so the
                // one-off CSS pass can style it as a fallback rather than the
                // stored fact the plain cell above is.
                const joined = emailBySub.get(row.user_sub);
                if (joined) {
                    return (
                        <span
                            className={`${styles.emailCell} ${styles.emailCellJoined}`}
                            title={t('console.hierarchy.scopedAccounts.table.emailFromRealmJoin', {
                                email: joined,
                            })}
                        >
                            {joined}
                        </span>
                    );
                }
                return (
                    <span className="portal-accounts-unavailable-cell">
                        {t('console.hierarchy.scopedAccounts.table.noEmail')}
                    </span>
                );
            },
        },
        {
            key: 'org',
            hideKey: 'org',
            width: 130,
            title: t('console.hierarchy.scopedAccounts.table.columnOrg'),
            render: (_value, row: ScopedAccountGroupedRow) => {
                // EAA-1839 (2026-08-24, user request): PLAIN text, not a `Chip` — full
                // "org1, org2, org3" whenever the column is wide enough (including
                // after a manual resize) to hold it, native `title` for hover, and
                // "orgName +N" ONLY as a fallback once `OrgListCell` measures that the
                // full list genuinely does not fit. `orgIds`/`orgNameById` order puts
                // THIS row's own org first — see `types.ts`'s `groupScopedAccountRows`
                // for why one account can hold several.
                const allOrgNames = (row.group?.orgIds ?? [row.organization_id]).map(
                    (id) => vm.orgNameById.get(id) ?? `${id.slice(0, 8)}…`
                );
                return <OrgListCell orgNames={allOrgNames} title={allOrgNames.join(', ')} />;
            },
        },
        {
            key: 'level',
            hideKey: 'level',
            title: t('console.hierarchy.scopedAccounts.table.columnLevel'),
            // EAA-1839 (Level column rework, 2026-08-21): this column used to show
            // `level_name` — free text with no server-side length limit, and real
            // data runs to "Unassigned (ach, auto-synced pending role mapping)".
            // Worse, EVERY row in a given org shares close to the same long
            // name, so at any width short of ~340px the clamp reads as an
            // identical "Unassigned (ac" pill on every row — it distinguished
            // nothing. `role_key` (e.g. "unassigned-ach") is the field that
            // actually differs row to row and is short enough to show in full
            // here (see `levelChipTextOf`); `level_name` moved into the
            // `Popover` below instead of being dropped. 110px comfortably fits
            // this tenant's `role_key` values without wrapping or clamping —
            // narrower than the old 140px, which only existed to (barely) fit
            // `level_name`. Still fixed, not auto: a resizable default per
            // `resize.storageKey` below, not a floor.
            /* EAA-1839: 140, not 110. Measured in the running app: at 113px the
             * real `role_key` (`unassigned-ach`) rendered as "unass…", which defeats
             * the entire reason this cell shows the role key instead of the level
             * name — legibility at a narrow width. Paid for out of `email` above. */
            width: 140,
            render: (_value, row) => {
                if (row.level_name) {
                    // EAA-1839: the chip's own text (`levelChipTextOf`) is short
                    // enough to not need truncation in the common case, but the
                    // the module's `.levelCellNameText` ellipsis stays as a safety net
                    // for an org whose `role_key` happens to run long. The full
                    // detail (`level_name` + `role_key`) lives in this `Popover`
                    // — switched from antd `Tooltip`, which was measured to
                    // render its content INLINE in the row's normal flow instead
                    // of in a floating layer (see this file's PR notes / the
                    // harness measurement): a screenshot with nothing hovered
                    // showed every chip clipping cleanly, but hovering down the
                    // column painted the tooltip's own text across the
                    // neighbouring "Tình trạng"/"Đăng nhập" cells the pointer had
                    // crossed. `Popover` is the proven-working floating layer on
                    // THIS screen — the export popover and `InlineLevelPicker`
                    // both already position correctly with it — so it was
                    // reached for instead of debugging `Tooltip` further.
                    // `HoverCard` (`@/components/ui`) was considered and
                    // rejected: it hardcodes `trigger="hover"` with no built-in
                    // way to also open on focus, and adding one would mean
                    // editing `packages/ui`, outside this ticket's owned files.
                    // `trigger={['hover', 'focus']}` here (antd's own default is
                    // `'hover'` alone) keeps this keyboard-reachable the same
                    // way the old `Tooltip` was — the wrapping span still
                    // carries `tabIndex={0}` since `Chip` itself takes no such
                    // prop. A native `title` is NOT also set here (unlike the
                    // `email` column below): stacking one on top of the popover
                    // would show both, which this task explicitly forbids.
                    // EAA-1839 (2026-08-25, user report: "action 'Đổi level' not
                    // working"): a row that ALREADY has a Level must still host an
                    // `InlineLevelPicker`, because the kebab's "Đổi Level" calls
                    // `picker.open(row)` and the picker only renders where an
                    // anchor for that `rowId` is mounted. Previously the anchor
                    // existed solely in the no-Level branch below (the amber "Chọn
                    // Level" chip), so on exactly the rows the menu item is FOR —
                    // the ones with a Level to change — the click set state that
                    // nothing rendered, and nothing happened at all.
                    //
                    // Two nested popovers on one cell is intentional and safe:
                    // the outer one is the picker (controlled, `trigger="click"`,
                    // and it wraps `children` in its own span) and the inner is
                    // the hover/focus detail card. Different triggers, different
                    // anchors.
                    return (
                        <InlineLevelPicker
                            picker={picker}
                            rowId={row.id}
                            identityLabel={identityPrimaryOf(row, displayNameBySub, emailBySub, t)}
                            orgLabel={vm.orgNameById.get(row.organization_id) ?? row.organization_id}
                        >
                        <Popover
                            content={levelPopoverContent(row, t)}
                            trigger={['hover', 'focus']}
                            overlayClassName="sa-level-info-popover"
                        >
                            <span
                                className="sa-level-cell"
                                tabIndex={0}
                                aria-label={t(
                                    'console.hierarchy.scopedAccounts.table.levelPopoverAriaLabel'
                                )}
                            >
                                {/* EAA-1839 (2026-08-24, user report: "chip css so ugly"):
                                    the chip rendered every Level in the SUCCESS
                                    (`mapped`, green) tone by omitting `tone`
                                    entirely, including the placeholder sentinel
                                    `unassigned-<app>` — a value that literally
                                    means "not mapped yet". A green pill saying
                                    "unassigned" reads as the UI contradicting
                                    itself, which is why it looked wrong rather
                                    than merely plain: the fix is the tone this
                                    board's OWN `unmapped` chip (frame 35a's
                                    "Chọn Level" trigger, a few lines below) already
                                    uses for the identical concept, not new CSS.
                                    `isUnassignedRoleKey` is the same
                                    classification `AppDetailAccountsTab` already
                                    applies to this exact backend sentinel
                                    (`UserSyncService.placeholder_role_key`),
                                    promoted into `Accounts/Shared/` so both boards
                                    agree by construction. */}
                                <Chip
                                    className={styles.levelCellName}
                                    tone={isUnassignedRoleKey(row.role_key) ? 'unmapped' : 'mapped'}
                                    // EAA-1839 (2026-08-24, user request): plain pill, no glyph — the
                                    // colour already carries the tone, and this chip sits right next to
                                    // a `Popover` that spells the full state out, so the ⚠ read as
                                    // noise rather than information.
                                    icon={false}
                                >
                                    <span className={styles.levelCellNameText}>
                                        {levelChipTextOf(row)}
                                    </span>
                                </Chip>
                            </span>
                        </Popover>
                        </InlineLevelPicker>
                    );
                }
                if (!vm.canManage || !row.is_active) {
                    return (
                        <span className={shared.warnText}>
                            {t('console.hierarchy.scopedAccounts.table.noLevel')}
                        </span>
                    );
                }
                return (
                    <InlineLevelPicker
                        picker={picker}
                        rowId={row.id}
                        identityLabel={identityPrimaryOf(row, displayNameBySub, emailBySub, t)}
                        orgLabel={vm.orgNameById.get(row.organization_id) ?? row.organization_id}
                    >
                        <Chip
                            className={styles.levelChip}
                            tone="unmapped"
                            onClick={() => picker.open(row)}
                        >
                            {t('console.hierarchy.scopedAccounts.table.pickLevelChip')}
                        </Chip>
                    </InlineLevelPicker>
                );
            },
        },
        {
            key: 'sync_status',
            hideKey: 'sync_status',
            width: 140,
            title: t('console.hierarchy.scopedAccounts.table.columnStatus'),
            sorter: true,
            render: (_value, row) => {
                const status = statusOf(row);
                return <StatusChip tone={statusToneOf(status)} label={statusTextOf(t, status)} />;
            },
        },
        {
            key: 'source',
            hideKey: 'source',
            width: 140,
            title: t('console.hierarchy.scopedAccounts.table.columnSource'),
            render: () => (
                <span
                    className="portal-accounts-unavailable-cell"
                    title={t('console.hierarchy.scopedAccounts.table.sourceUnavailable')}
                >
                    {t('console.hierarchy.scopedAccounts.table.lastLoginEmpty')}
                </span>
            ),
        },
        {
            key: 'last_login',
            hideKey: 'last_login',
            width: 140,
            title: t('console.hierarchy.scopedAccounts.table.columnLastLogin'),
            render: () => (
                <span
                    className="portal-accounts-unavailable-cell"
                    title={t('console.hierarchy.scopedAccounts.table.lastLoginUnavailable')}
                >
                    {t('console.hierarchy.scopedAccounts.table.lastLoginEmpty')}
                </span>
            ),
        },
    ];

    const columns = allColumns.filter(
        (column) => !column.hideKey || views.isColumnVisible(column.hideKey as never)
    );

    return (
        <>
            {/* EAA-1839 (task 4): the username join is a capped realm-page walk
                (see `useScopedAccountUsernames`'s docstring) — surfaced the same
                way the search walk's own cap is surfaced elsewhere on this screen
                (`ExportAccountsPopover`'s `cappedWarning` Callout), so a row that
                shows an email instead of a username does not read as "this
                account has no username" when it may just mean "the walk stopped
                before reaching it". */}
            {usernameJoinCapped && (
                // EAA-1839: `className` straight onto the Callout — it extends
                // `Styleable` now, so the positioning wrapper this needed is gone.
                <Callout className={styles.usernameJoinCapped} tone="warning" longLabel>
                    {t('console.hierarchy.scopedAccounts.table.usernameJoinCapped')}
                </Callout>
            )}
            <SharedAccountsTable<ScopedAccountGroupedRow>
                rows={vm.rows}
                columns={columns}
                rowKey="id"
                // EAA-1839: per-viewer persisted column widths for this table's own
                // key (`ui:colwidths:scoped-accounts`); no `version` bump needed here
                // since the column set's shape has not changed.
                resize={{ storageKey: 'scoped-accounts' }}
                className={`sa-table--${views.density}`}
                rowClassName={(row) => {
                    if (!row.is_active) return 'sa-row--revoked';
                    if (row.sync_status === 'error') return 'sa-row--error';
                    return '';
                }}
                selectedKeys={[...vm.selected.keys()]}
                onSelectedKeysChange={(keys) => {
                    // EAA-1820: the shared table's selection callback is key-only
                    // (`DataTable`'s own `rowSelection.onChange` shape), but this
                    // screen's selection model is a Map<id, ScopedAccountRow> — bulk
                    // Level-assign and "select all matching" both need the whole row,
                    // not just its id (see `useScopedAccounts.selected`). `toggleRow`
                    // already flips exactly one row in/out of that map, so reconcile
                    // by toggling every row on THIS page whose ticked state changed —
                    // off-page rows already selected via "select all matching" are
                    // untouched since they are not in `vm.rows`.
                    const nextIds = new Set(keys);
                    for (const row of vm.rows) {
                        const wasSelected = vm.selected.has(row.id);
                        const nowSelected = nextIds.has(row.id);
                        if (wasSelected !== nowSelected) vm.toggleRow(row);
                    }
                }}
                onViewDetail={onViewDetail}
                rowActionsAriaLabel={(row) =>
                    t('console.hierarchy.scopedAccounts.table.rowActionsAriaLabel', {
                        identity: row.user_email || row.user_sub,
                    })
                }
                rowActions={(row) => rowActionItems(row, vm, writes, onTransfer, picker.open, t)}
                emptyText={t('console.accounts.table.emptyText')}
            />
        </>
    );
}

export default ScopedAccountsTable;
